import React from "react";
import { Descriptions, Space, Tag, Typography } from "antd";
import { CONFIG_LABELS } from "../institutionMerge.constants";
import { userGroupPending } from "./pending";

const { Text } = Typography;

const OID = /^[a-f0-9]{24}$/i;
const show = (v, names = {}) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "oui" : "non";
  if (Array.isArray(v))
    return v.length ? v.map(id => names[id] || String(id)).join(", ") : "—";
  if (typeof v === "string" && OID.test(v) && names[v]) return names[v];
  if (typeof v === "object" && "cloturePrestation" in v)
    return `clôture prestation : ${v.cloturePrestation ? "oui" : "non"}`;
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

/**
 * What this merge will do, in one glance — sources archived, the new institution and its
 * chosen configuration, and every decision taken. Shown on the preview step and again
 * right above the Apply button, so nothing is confirmed without being restated.
 */
export function MergeSummary({ preview, decisions }) {
  const d = decisions || {};
  const cfg = preview.config.fields;
  const names = preview.config.refNames || {};
  // name and billings have their own cells; every other field is listed with its retained value
  const chosen = Object.entries(cfg)
    .filter(([field]) => field !== "name" && field !== "billings")
    .map(([field, f]) => ({
      field,
      value:
        d.config && d.config[field] !== undefined
          ? d.config[field]
          : f.suggested,
      conflict: f.conflict,
    }));
  const dupCount = preview.users.duplicates.length;
  // a group counts as decided only when its action, survivor and every differing field are settled — same helper as the step counters
  const fieldsList =
    Array.isArray(preview.users.fields) && preview.users.fields.length
      ? preview.users.fields
      : null;
  const dupDecided = preview.users.duplicates.filter(
    g => !userGroupPending(g, d.users && d.users[g.email], fieldsList),
  ).length;
  // decided (even empty: « aucune règle datée ») wins; undecided = the union timeline, like the server
  const timeline = Array.isArray(d.billings)
    ? d.billings
    : preview.billings.timeline.filter(t => t.type);
  const billingConflictsLeft = preview.billings.conflicts.filter(
    c =>
      !(Array.isArray(d.billings) ? d.billings : []).some(
        b => b.year === c.year && b.month === c.month && b.type,
      ),
  ).length;
  const blCount = preview.blacklists.length;
  const blKeep = preview.blacklists.filter(
    b => d.blacklists && d.blacklists[b.interpreterId] === "keep",
  ).length;
  const blDrop = preview.blacklists.filter(
    b => d.blacklists && d.blacklists[b.interpreterId] === "drop",
  ).length;

  return (
    <Descriptions bordered size="small" column={2}>
      <Descriptions.Item label="Établissements archivés" span={2}>
        <Space wrap>
          {preview.sources.map(s => (
            <Tag key={s._id}>{s.name}</Tag>
          ))}
        </Space>
      </Descriptions.Item>
      <Descriptions.Item
        label={
          preview.existingTarget
            ? "Institution qui reçoit la fusion"
            : "Nouvelle institution"
        }
      >
        {preview.existingTarget ? (
          <Space>
            <Text strong>{preview.existingTarget.name}</Text>
            <Tag>existante — son nom ne change pas</Tag>
          </Space>
        ) : d.targetName ? (
          <Text strong>{d.targetName}</Text>
        ) : (
          <Text type="danger">nom manquant</Text>
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Référence configuration">
        {preview.configDonor ? preview.configDonor.name : "—"}
      </Descriptions.Item>
      <Descriptions.Item label="Enregistrements déplacés">
        <Text strong>{preview.totals.moves}</Text>
      </Descriptions.Item>
      <Descriptions.Item label="Règles de facturation">
        {timeline.length
          ? `${timeline.length} règle(s) datée(s) après fusion`
          : "aucune règle datée après fusion"}
        {preview.billings.conflicts.length
          ? ` — ${preview.billings.conflicts.length} mois en conflit, ${billingConflictsLeft} à choisir`
          : ""}
      </Descriptions.Item>
      <Descriptions.Item label="Comptes en double">
        {dupCount ? `${dupDecided}/${dupCount} décidé(s)` : "aucun"}
      </Descriptions.Item>
      <Descriptions.Item label="Listes noires">
        {blCount
          ? `${blKeep} gardée(s), ${blDrop} retirée(s) sur ${blCount}`
          : "aucune"}
      </Descriptions.Item>
      <Descriptions.Item label="Configuration retenue" span={2}>
        <Space wrap size={[4, 4]}>
          {chosen.map(c => (
            <Tag key={c.field} color={c.conflict ? "blue" : undefined}>
              {CONFIG_LABELS[c.field] || c.field}: {show(c.value, names)}
            </Tag>
          ))}
        </Space>
      </Descriptions.Item>
    </Descriptions>
  );
}
